import React, { useState } from 'react';
import { useAuth } from '../App';
import { X, Phone, Mail, ArrowRight, Loader2, RefreshCw, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthModal({ onClose }) {
  const { login } = useAuth();
  const [step, setStep] = useState('identifier'); // identifier -> otp -> signup
  const [inputMode, setInputMode] = useState('phone'); // phone | email
  const [phone, setPhone] = useState('');
  const [emailVal, setEmailVal] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cleanPhone = phone.replace(/\D/g, '').slice(-10);
  const fullPhone = `+91${cleanPhone}`;
  const identifier = inputMode === 'phone' ? fullPhone : emailVal.trim().toLowerCase();
  const displayId = inputMode === 'phone' ? `+91 ${cleanPhone}` : emailVal;

  const finishLogin = (user) => {
    if (!user.name || user.name === 'Pet Parent') {
      setStep('signup');
    } else {
      onClose();
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (inputMode === 'phone' && cleanPhone.length < 10) { setError('Enter a valid 10-digit phone number'); return; }
    if (inputMode === 'email' && (!emailVal.trim() || !emailVal.includes('@'))) { setError('Enter a valid email address'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send OTP');
      setStep('otp');
    } catch (err) { setError(err.message || 'Failed to send OTP'); }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) { setError('Enter the OTP'); return; }
    setLoading(true);
    setError('');
    try {
      const user = await login(identifier, otp, null, null);
      finishLogin(user);
    } catch (err) { setError(err.message || 'Invalid OTP'); }
    setLoading(false);
  };

  const handleSignupComplete = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name'); return; }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('petfolk_token');
      const res = await fetch(`${API}/api/auth/update-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), email: emailVal.trim() || null }),
      });
      if (!res.ok) throw new Error('Failed to update profile');
      onClose();
      window.location.reload();
    } catch (err) { setError(err.message || 'Update failed'); }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/resend-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Resend failed');
    } catch (err) { setError(err.message || 'Resend failed'); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" data-testid="auth-modal" onClick={onClose}>
      <div className="petfolk-card w-full max-w-md mx-4 p-8 relative animate-slide-up" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5" data-testid="auth-close-btn">
          <X size={20} />
        </button>

        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-petfolk-orange to-petfolk-purple flex items-center justify-center mx-auto mb-3">
            {step === 'signup' ? <User size={24} color="white" /> : inputMode === 'email' ? <Mail size={24} color="white" /> : <Phone size={24} color="white" />}
          </div>
          <h2 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {step === 'identifier' ? 'Welcome to Petfolk' : step === 'otp' ? 'Verify OTP' : 'Almost There!'}
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            {step === 'identifier' ? 'Sign in with your phone number or email'
              : step === 'otp' ? `Enter the OTP sent to ${displayId}`
              : 'Tell us your name to get started'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 text-sm" data-testid="auth-error">
            {error}
          </div>
        )}

        {step === 'identifier' && (
          <form onSubmit={handleSendOTP} className="space-y-4">
            <div className="flex rounded-xl overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
              <button type="button" data-testid="auth-mode-phone"
                onClick={() => { setInputMode('phone'); setError(''); }}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${inputMode === 'phone' ? 'text-white' : ''}`}
                style={inputMode === 'phone' ? { background: 'var(--petfolk-orange)' } : { color: 'var(--text-secondary)' }}>
                <Phone size={14} /> Phone
              </button>
              <button type="button" data-testid="auth-mode-email"
                onClick={() => { setInputMode('email'); setError(''); }}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${inputMode === 'email' ? 'text-white' : ''}`}
                style={inputMode === 'email' ? { background: 'var(--petfolk-orange)' } : { color: 'var(--text-secondary)' }}>
                <Mail size={14} /> Email
              </button>
            </div>

            {inputMode === 'phone' ? (
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Phone Number</label>
                <div className="flex gap-2">
                  <div className="petfolk-input flex items-center justify-center text-sm font-medium shrink-0" style={{ color: 'var(--text-secondary)', width: '56px', minWidth: '56px', padding: '12px 6px' }}>+91</div>
                  <input data-testid="auth-phone-input" className="petfolk-input flex-1 min-w-0" placeholder="9876543210"
                    value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} type="tel" required autoFocus />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Email Address</label>
                <input data-testid="auth-email-input" className="petfolk-input" placeholder="you@example.com"
                  value={emailVal} onChange={e => setEmailVal(e.target.value)} type="email" required autoFocus />
              </div>
            )}

            <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid="auth-send-otp-btn">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <><ArrowRight size={18} /> Send OTP</>}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>OTP Code</label>
              <input data-testid="auth-otp-input" className="petfolk-input text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="----" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))} maxLength={4} autoFocus />
            </div>
            <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid="auth-verify-otp-btn">
              {loading ? <Loader2 size={18} className="animate-spin" /> : 'Verify & Sign In'}
            </button>
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => { setStep('identifier'); setOtp(''); setError(''); }}
                className="text-sm" style={{ color: 'var(--petfolk-orange)' }} data-testid="auth-change-identifier">
                {inputMode === 'phone' ? 'Change number' : 'Change email'}
              </button>
              <button type="button" onClick={handleResendOTP} disabled={loading} className="text-sm flex items-center gap-1" style={{ color: 'var(--text-secondary)' }} data-testid="auth-resend-otp">
                <RefreshCw size={12} /> Resend OTP
              </button>
            </div>
          </form>
        )}

        {step === 'signup' && (
          <form onSubmit={handleSignupComplete} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Your Name *</label>
              <input data-testid="auth-name-input" className="petfolk-input" placeholder="What should we call you?"
                value={name} onChange={e => setName(e.target.value)} required autoFocus />
            </div>
            {inputMode === 'phone' && (
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Email (optional)</label>
                <input data-testid="auth-signup-email-input" className="petfolk-input" placeholder="you@example.com"
                  value={emailVal} onChange={e => setEmailVal(e.target.value)} type="email" />
              </div>
            )}
            <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid="auth-signup-btn">
              {loading ? <Loader2 size={18} className="animate-spin" /> : 'Get Started'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
